import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';

export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password-callback`,
      });

      if (error) throw error;

      setEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      if (typeof err === 'object' && err !== null && 'message' in err) {
        setError((err as any).message);
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-[#c9fffc]/10 shadow-2xl">
          {/* Back to login link */}
          <div className="mb-6">
            <a
              href="/"
              className="inline-flex items-center text-gray-400 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </a>
          </div>

          {emailSent ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-[#c9fffc]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-[#c9fffc]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
              <p className="text-gray-400 mb-4">
                We've sent a password reset link to <span className="text-[#c9fffc]">{email}</span>
              </p>
              <p className="text-gray-400 mb-6">
                Click the link in the email to reset your password.
              </p>
              <a
                href="/"
                className="inline-flex items-center justify-center w-full px-4 py-3 bg-[#c9fffc] text-gray-900 rounded-xl hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] font-medium transition-colors"
              >
                Back to Sign In
              </a>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Reset Password</h2>
                <p className="text-gray-400">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-[#c9fffc]/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#c9fffc] focus:border-transparent transition-colors"
                    placeholder="Enter your email address"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full flex items-center justify-center px-4 py-3 bg-[#c9fffc] text-gray-900 rounded-xl hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] font-medium shadow-lg shadow-[#c9fffc]/10 transition-all disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Send Reset Link
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 